import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { LabeledFilterSelect } from '@backrs_ui/common-ui';

import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { StandardsByTrackContent } from '@backrs_ui/common-ui/src/molecules/AssessMyTrack/StandardsByTrackModal';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useGetDimensionCriteriaQuery,
  useGetTrackAssessmentStandardsQuery,
} from '@backrs_ui/grow-ui/src/redux/trackAssessment/trackAssessment.api';

import { Alert, Box, SxProps, Theme } from '@mui/material';
import Lottie from 'lottie-react';

import { benchmarkOptions, buildBenchmarkRubrics, findBenchmarkRubric } from './benchmarkRubrics';
import { useAgentContext } from './BenchmarksPageContainer.agentContext';
import { useCanViewBenchmarks } from './useCanViewBenchmarks';

const STRINGS = appStrings.modules.benchmarks;

export const BenchmarksPageContainer = () => {
  const canView = useCanViewBenchmarks();
  const skip = canView !== true;

  const { data: standards, isError: isStandardsError } = useGetTrackAssessmentStandardsQuery(
    undefined,
    { skip },
  );
  const { data: criteria, isError: isCriteriaError } = useGetDimensionCriteriaQuery({}, { skip });

  const rubrics = useMemo(
    () => (standards && criteria ? buildBenchmarkRubrics(standards, criteria) : undefined),
    [standards, criteria],
  );

  const { categories, years } = useMemo(() => benchmarkOptions(rubrics ?? []), [rubrics]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<number>();
  const [selectedYearId, setSelectedYearId] = useState<number>();

  const category = categories.find((c) => c.id === selectedCategoryId) ?? categories[0];
  const year = years.find((y) => y.id === selectedYearId) ?? years[0];
  const rubric =
    rubrics && category && year ? findBenchmarkRubric(rubrics, category.id, year) : undefined;

  useAgentContext(rubric);

  if (canView === undefined) return null;
  if (!canView) return <Navigate to={APP_ROUTE.HOME} replace />;

  if (isStandardsError || isCriteriaError) {
    return <Alert severity="error">{STRINGS.loadError}</Alert>;
  }

  if (!rubrics) {
    return (
      <Box sx={loadingSx}>
        <Lottie animationData={lottieAnimations.dotsDotting} />
      </Box>
    );
  }

  return (
    <Box sx={pageSx}>
      <Box sx={selectsSx}>
        <Box sx={unlockSelectSx}>
          <LabeledFilterSelect
            id="benchmarks-unlock-select"
            label={STRINGS.unlockLabel}
            value={String(category?.id ?? '')}
            options={categories.map(({ id, name }) => ({ value: String(id), label: name }))}
            onChange={(value) => setSelectedCategoryId(Number(value))}
          />
        </Box>
        <Box sx={yearSelectSx}>
          <LabeledFilterSelect
            id="benchmarks-year-select"
            label={STRINGS.yearLabel}
            value={String(year?.id ?? '')}
            options={years.map(({ id, label }) => ({ value: String(id), label }))}
            onChange={(value) => setSelectedYearId(Number(value))}
          />
        </Box>
      </Box>
      {rubric && (
        <Box sx={cardSx}>
          <StandardsByTrackContent category={rubric.category} variant={rubric.variant} />
        </Box>
      )}
    </Box>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const selectsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
});

const unlockSelectSx: SxProps<Theme> = {
  flex: '1 1 240px',
  maxWidth: '360px',
};

const yearSelectSx: SxProps<Theme> = {
  flex: '1 1 180px',
  maxWidth: '220px',
};

// matches the modal's padding, which the table bleeds into
const cardSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-3xl'],
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
});

const loadingSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
};
